import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('worldpay_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const fetchAdmin = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setAdmin(response.data);
        } catch (error) {
          console.error('Failed to fetch admin:', error);
          logout();
        } finally {
          setLoading(false);
        }
      };
      fetchAdmin();
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCurrentAdmin = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;

    const response = await axios.post(`${API}/auth/login`, payload);

    if (response.data.requires_2fa) {
      return { requires_2fa: true };
    }

    const { token: newToken, admin: adminData } = response.data;
    localStorage.setItem('worldpay_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setAdmin(adminData);
    return adminData;
  };

  const logout = () => {
    localStorage.removeItem('worldpay_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setAdmin(null);
  };

  const refreshAdmin = async () => {
    await fetchCurrentAdmin();
  };

  const value = {
    admin,
    token,
    loading,
    login,
    logout,
    refreshAdmin,
    isAuthenticated: !!token && !!admin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
